// src/store/modules/admin.js
import apiService from '@/services/api.service';

const state = {
  dashboard: {
    stats: {
      clientCount: 0,
      deviceCount: 0,
      offlineCount: 0
    },
    latestEvents: []
  },
  clients: [],
  unassignedDevices: [],
  loading: false,
  error: null
};

const getters = {
  dashboard: state => state.dashboard,
  clients: state => state.clients,
  unassignedDevices: state => state.unassignedDevices,
  loading: state => state.loading,
  error: state => state.error
};

const actions = {
  // Récupérer les données du tableau de bord
  async fetchDashboard({ commit }) {
    commit('setLoading', true);
    commit('clearError');
    
    try {
      const response = await apiService.getAdminDashboard();
      commit('setDashboard', response.data);
      return response.data;
    } catch (error) {
      commit('setError', error.response ? error.response.data.message : 'Erreur de chargement du tableau de bord');
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },
  
  // Récupérer tous les clients
  async fetchClients({ commit }) {
    commit('setLoading', true);
    commit('clearError');
    
    try {
      const response = await apiService.getClients();
      commit('setClients', response.data);
      return response.data;
    } catch (error) {
      commit('setError', error.response ? error.response.data.message : 'Erreur de chargement des clients');
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },
  
  // Rechercher des clients
  async searchClients({ commit }, query) {
    commit('setLoading', true);
    commit('clearError');
    
    try {
      const response = await apiService.searchClients(query);
      commit('setClients', response.data);
      return response.data;
    } catch (error) {
      commit('setError', error.response ? error.response.data.message : 'Erreur de recherche des clients');
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },
  
  // Récupérer les détails d'un client
  async fetchClientDetails({ commit }, clientId) {
    commit('setLoading', true);
    commit('clearError');
    
    try {
      const response = await apiService.getClientDetails(clientId);
      return response.data;
    } catch (error) {
      commit('setError', error.response ? error.response.data.message : 'Erreur de chargement des détails du client');
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },
  
  // Créer un client
  async createClient({ commit }, clientData) {
    commit('setLoading', true);
    commit('clearError');
    
    try {
      const response = await apiService.createClient(clientData);
      return response.data;
    } catch (error) {
      commit('setError', error.response ? error.response.data.message : 'Erreur de création du client');
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },
  
  // Mettre à jour un client
  async updateClient({ commit }, { clientId, clientData }) {
    commit('setLoading', true);
    commit('clearError');
    
    try {
      const response = await apiService.updateClient(clientId, clientData);
      return response.data;
    } catch (error) {
      commit('setError', error.response ? error.response.data.message : 'Erreur de mise à jour du client');
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },
  
  // Activer/désactiver un client
  async toggleClientStatus({ commit }, { clientId, active }) {
    commit('setLoading', true);
    commit('clearError');
    
    try {
      const response = await apiService.toggleClientStatus(clientId, active);
      return response.data;
    } catch (error) {
      commit('setError', error.response ? error.response.data.message : 'Erreur de changement de statut du client');
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },
  
  // Supprimer un client
  async deleteClient({ commit }, clientId) {
    commit('setLoading', true);
    commit('clearError');
    
    try {
      const response = await apiService.deleteClient(clientId);
      return response.data;
    } catch (error) {
      commit('setError', error.response ? error.response.data.message : 'Erreur de suppression du client');
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },
  
  // Récupérer tous les événements
  async fetchAllEvents({ commit }) {
    commit('setLoading', true);
    commit('clearError');
    
    try {
      const response = await apiService.getAllEvents();
      return response.data;
    } catch (error) {
      commit('setError', error.response ? error.response.data.message : 'Erreur de chargement des événements');
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },

  // Cartes provisionnées en atelier et pas encore attribuées. Elles publient déjà : sans cette
  // file, elles parlent sans que rien ne les écoute.
  async fetchUnassignedDevices({ commit }) {
    try {
      const response = await apiService.getUnassignedDevices();
      commit('setUnassignedDevices', response.data);
      return response.data;
    } catch (error) {
      commit('setError', error.response ? error.response.data.message : 'Erreur de chargement des cartes non attribuées');
      throw error;
    }
  },

  async attachDevice({ dispatch }, { imei, client_id, name, location }) {
    const response = await apiService.attachDevice(imei, { client_id, name, location });
    await dispatch('fetchUnassignedDevices');
    return response.data;
  },

  // Volontairement SANS mutation d'état : le code en clair ne doit pas séjourner dans le
  // store, où il survivrait à la fermeture de l'étiquette et se retrouverait dans n'importe
  // quel outil d'inspection. Il vit le temps du composant, et disparaît avec lui.
  async issueClaimCode(_ctx, { imei, email }) {
    const response = await apiService.issueClaimCode(imei, email ? { email } : {});
    return response.data;
  }
};

const mutations = {
  setUnassignedDevices(state, devices) {
    state.unassignedDevices = devices;
  },
  setDashboard(state, dashboard) {
    state.dashboard = dashboard;
  },
  setClients(state, clients) {
    state.clients = clients;
  },
  setLoading(state, status) {
    state.loading = status;
  },
  setError(state, error) {
    state.error = error;
  },
  clearError(state) {
    state.error = null;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};